import { useContext, useState, useEffect, cloneElement, useRef } from 'react';
import { Steps, Button, Spin, Tooltip, Modal } from 'antd';

import { getReturnLabel } from 'actions/products';
import { FinishCheckGreenSVG } from 'components/iconsSVG/finishCheckGreen';
import { InfoWhiteSVG } from 'components/iconsSVG/infoWhite';
import { i18nContext } from 'context/i18n';
import { openBase64, getMarket, autoOpenLink } from 'utils/helpers';
import { getServerError } from 'utils/request';
import type { TOrder } from 'types/order';
import { Button as UIButton } from 'components/ui/button';
import { Link } from 'components/ui/link';
import { getDomainValue } from 'utils/domain';

import styles from 'styles/OrderDetails.module.css';

const { Step } = Steps;

const WarnStep = <Step status="wait" className={styles.statusStepWarn} />;
const FinishStep = <Step status="finish" className={styles.stepWithIcon} icon={<FinishCheckGreenSVG />} />;
const ProcessStep = <Step status="process" />;
const ErrorStep = <Step status="error" />;

const fullFinished = [FinishStep, FinishStep, FinishStep, FinishStep];

const FullFinishedStatus = {
  status: 'finish',
  steps: fullFinished,
};

export const STEPS_COMPONENTS_PROPS = {
  CANCELLED: {
    status: 'error',
    steps: [FinishStep, ErrorStep, null, null],
  },
  PENDING: {
    status: 'process',
    steps: [ProcessStep, null, null, null],
  },
  PENDING_CUSTOMER: {
    status: 'process',
    steps: [WarnStep, null, null, null],
  },
  IN_PREPARATION: {
    status: 'finish',
    steps: [FinishStep, ProcessStep, null, null],
  },
  PENDING_DELIVERY: {
    status: 'finish',
    steps: [FinishStep, FinishStep, ProcessStep, null],
  },
  OUT_FOR_DELIVERY: {
    status: 'finish',
    steps: [FinishStep, FinishStep, FinishStep, ProcessStep],
  },
  DELIVERED_PENDING: {
    status: 'finish',
    steps: [FinishStep, FinishStep, ProcessStep, null],
  },
  NOT_ACCEPTED: {
    status: 'error',
    steps: [ErrorStep, null, null, null],
  },
  REGISTERED: {
    status: 'finish',
    steps: [FinishStep, FinishStep, ProcessStep, null],
  },
  RECEIVED: {
    status: 'finish',
    steps: [FinishStep, FinishStep, FinishStep, ProcessStep],
  },
  DELIVERED: FullFinishedStatus,
  COLLECTED: FullFinishedStatus,
  ACCEPTED: FullFinishedStatus,
};

type GeneralStatusComponentProps = {
  title: string;
  status: string;
  steps: any[];
};
export const GeneralStatusComponent = ({ title, status, steps }: GeneralStatusComponentProps) => {
  const titleClasses = {
    error: styles.statusError,
    process: styles.statusWarn,
    finish: styles.statusSuccess,
  };

  const bodyClasses = {
    finish: styles.orderSuccess,
  };

  return (
    <div>
      <p className={`${styles.orderStatusTitle} ${titleClasses[status]}`}>{title}</p>
      {/* @ts-ignore Antd type error */}
      <Steps
        responsive={false}
        direction="horizontal"
        current={0}
        status="process"
        size="small"
        className={`${styles.orderStatus} ${bodyClasses[status]} !px-1`}
      >
        {steps.map((item, i) => (item !== null ? cloneElement(item, { key: `general-status-step-${i}` }) : <Step key={`general-status-step-${i}`} />))}
      </Steps>
    </div>
  );
};

type ConfirmationComponentProps = {
  confirmationBlock: string;
  confirmationBlockText: string;
  confirmationBlockTitle: string;
  handleConfirmation: (confirmationBlock: string) => void;
  handleRejection: () => void;
  loading: boolean;
  hasIdTransaction: boolean;
  idTransaction: string;
};
export const ConfirmationComponent = ({
  confirmationBlock,
  confirmationBlockText,
  confirmationBlockTitle,
  handleConfirmation,
  loading,
  handleRejection,
  hasIdTransaction,
  idTransaction,
}: ConfirmationComponentProps) => {
  const { t } = useContext(i18nContext);
  const withButtons = typeof confirmationBlock === 'string' && confirmationBlock.startsWith('CONFIRM');

  if (confirmationBlockTitle && confirmationBlockTitle !== '') {
    return (
      // @ts-ignore Antd type error
      <Spin spinning={loading}>
        <div className={styles.confirmationContainer}>
          <p className={styles.confirmationTitle}>{confirmationBlockTitle}</p>

          <div className={styles.confirmationContent}>
            <p className={styles.confirmationMessage}>{confirmationBlockText}</p>
            {withButtons && (
              <div className={styles.confirmationButtons}>
                <Button type="primary" onClick={() => handleConfirmation(confirmationBlock)} className={styles.acceptButton}>
                  {t('mis.pedidos', 'si', 'Yes')}
                </Button>
                <Button className={styles.rejectButton} onClick={handleRejection}>
                  {t('mis.pedidos', 'no', 'No')}
                </Button>
              </div>
            )}
            {hasIdTransaction && <p className={styles.idTransaction}>{idTransaction}</p>}
          </div>
        </div>
      </Spin>
    );
  }
  return <span></span>;
};

type OrderStatusInfoComponentProps = {
  orderDelivered: string;
  orderStatusName: string;
  orderStatusText: string;
  orderStatusInfo: string;
};
export const OrderStatusInfoComponent = ({ orderDelivered, orderStatusName, orderStatusText, orderStatusInfo }: OrderStatusInfoComponentProps) => {
  // let DateSubComponent = null;
  const additionalClass = STEPS_COMPONENTS_PROPS[orderStatusName]?.status;

  let content = null;

  if (orderDelivered !== undefined && orderDelivered && orderDelivered !== '0000-00-00') {
    const parsedDate = new Date(orderDelivered);
    const parsedDay = parsedDate.getDate();
    const parsedMonth = parsedDate.toLocaleString('default', { month: 'short' });

    content = (
      <div className={styles.orderStatusContainer}>
        <div className={styles.orderStatusContent}>
          <p className={styles.orderStatusInfoTitle}>{orderStatusText}</p>
          <p className={styles.orderStatusBody} dangerouslySetInnerHTML={{ __html: orderStatusInfo }} />
        </div>
        <div className={`${styles.orderInfo} ${styles[additionalClass]}`}>
          <p className={styles.infoText}>{orderStatusText}</p>
          <p className={styles.infoDate}>
            {parsedMonth.toUpperCase()}
            <span className={styles.infoDay}>{parsedDay}</span>
          </p>
        </div>
      </div>
    );
  } else {
    content = (
      <div className={`${styles.orderStatusContainer} ${styles[additionalClass]} ${styles.orderStatusNoDateContainer}`}>
        <div className={`${styles.orderStatusContent} ${styles.orderStatusNoDate}`}>
          <p className={styles.orderStatusInfoTitleNoDate}>{orderStatusText}</p>
          {orderStatusInfo && (
            <Tooltip
              placement="bottom"
              title={<span dangerouslySetInnerHTML={{ __html: orderStatusInfo }}></span>}
              overlayClassName={styles.tooltip}
              arrowPointAtCenter
            >
              <InfoWhiteSVG />
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  return content;
};

type TrackingComponentProps = {
  carrierUrl: string;
  trackingCode: string;
};
export const TrackingComponent = ({ carrierUrl, trackingCode }: TrackingComponentProps) => {
  const { t } = useContext(i18nContext);
  const [showTrackingMessage, setShowTrackingMessage] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  return (
    <div className={`flex flex-col gap-4 empty:hidden lab:my-4`} id={'print-no-margin'}>
      {carrierUrl !== '' && (
        <Link variant="outline" href={carrierUrl} id={'no-print-hide'} target="_blank" rel="noreferrer">
          {t('pedido', 'track.your.order', 'Track your order')}
        </Link>
      )}
      {trackingCode && (
        <>
          <UIButton
            variant="outline"
            className={getDomainValue({ camper: 'h-10 px-2 [&>span]:gap-1', nnormal: 'camper', camperlab: undefined })}
            onPress={() => {
              if (navigator.clipboard) {
                navigator.clipboard.writeText(String(trackingCode));
                setShowTrackingMessage(true);
                timer.current = setTimeout(() => {
                  setShowTrackingMessage(false);
                  timer.current = null;
                }, 2000);
              }
            }}
          >
            {showTrackingMessage ?
              t('pedido', 'tracking.copied', '**Tracking number copied to clipboard.')
            : <span>
                {t('pedido', 'copy.tracking', 'Copy tracking')} <span className="font-medium">{trackingCode}</span>
              </span>
            }
          </UIButton>
        </>
      )}
    </div>
  );
};

type LabelButtonsProps = {
  data: TOrder;
  order: { email: string; locator: string };
};
export const LabelButtons = ({ data }: LabelButtonsProps) => {
  const { t, locale } = useContext(i18nContext);
  const market = getMarket(locale);
  const [loadingLabel, setLoadingLabel] = useState(false);

  const handleLoadLabel = async (labelTitle: string) => {
    setLoadingLabel(true);

    try {
      const payload = {
        email: data?.applicant?.email,
        locator: data?.reference,
      };

      const labelRs = await getReturnLabel({
        profile: locale,
        ...payload,
      });

      if (labelRs?.status === 'KO') {
        throw getServerError(labelRs);
      }

      if (labelRs?.returnLabel) {
        openBase64(labelTitle, labelRs.returnLabel);
      } else if (labelRs?.pdfName) {
        autoOpenLink(labelRs.pdfName);
      } else {
        const error = getServerError(labelRs);
        throw error;
      }
    } catch (e) {
      console.error('Error en handleLoadLabel', e);
      Modal.error({
        title: t('mis.pedidos', 'error.cargando.etiqueta', '***Ha ocurrido un error.'),
        content: <p>{e.message}</p>,
      });
    }

    setLoadingLabel(false);
  };

  let labelContent = (
    <>
      {data.dropoffLabel && (
        <>
          <p>{t('mis.pedidos', 'imprimir.etiqueta.dropoff')}: </p>
          <Button
            data-dropoff
            onClick={() => handleLoadLabel(t('mis.pedidos', 'imprimir.etiqueta.dropoff'))}
            className={`${styles.returnLink} ${styles.printLabel}`}
          >
            {t('mis.pedidos', 'imprimir.etiqueta.dropoff')}
          </Button>
        </>
      )}
      {data.dhlParcelshopLabel && (
        <>
          <p>{t('mis.pedidos', 'imprimir.etiqueta.dhlparcelshop')}: </p>
          <Button onClick={() => handleLoadLabel(t('mis.pedidos', 'imprimir.etiqueta.dhlparcelshop'))} className={`${styles.returnLink} ${styles.printLabel}`}>
            {t('mis.pedidos', 'imprimir.etiqueta.dhlparcelshop.boton')}
          </Button>
        </>
      )}
      {data.ukmailLabel && (
        <>
          {t('mis.pedidos', 'imprimir.etiqueta.dhlparcelshopuk') !== '' && <p>{t('mis.pedidos', 'imprimir.etiqueta.dhlparcelshopuk')}: </p>}
          <Button
            onClick={() => handleLoadLabel(t('mis.pedidos', 'imprimir.etiqueta.dhlparcelshopuk'))}
            className={`${styles.returnLink} ${styles.printLabel}`}
          >
            {t('mis.pedidos', 'imprimir.etiqueta.dhlparcelshopuk.boton')}
          </Button>
        </>
      )}
      {data.correosLabel && (
        <>
          <p>{t('mis.pedidos', 'imprimir.etiqueta.correos')}: </p>
          <Button onClick={() => handleLoadLabel(t('mis.pedidos', 'imprimir.etiqueta.correos'))} className={`${styles.returnLink} ${styles.printLabel}`}>
            {t('mis.pedidos', 'imprimir.etiqueta.correos.boton')}
          </Button>
        </>
      )}
      {data.returnLabel && (
        <>
          <p>{t('mis.pedidos', `imprimir.etiqueta.${market}`)}: </p>
          <Button onClick={() => handleLoadLabel(t('mis.pedidos', `imprimir.etiqueta.${market}`))} className={`${styles.returnLink} ${styles.printLabel}`}>
            {t('mis.pedidos', `imprimir.etiqueta.${market}.boton`)}
          </Button>
        </>
      )}
    </>
  );

  if (data.labelPdfName) {
    labelContent = (
      <>
        <p>{t('mis.pedidos', `imprimir.etiqueta.${market}`)}: </p>
        <Button target="_blank" href={data.labelPdfName} className={`${styles.returnLink} ${styles.printLabel}`}>
          {t('mis.pedidos', `imprimir.etiqueta.${market}.boton`)}
        </Button>
      </>
    );
  }

  // @ts-ignore Antd type error
  return <Spin spinning={loadingLabel}>{labelContent}</Spin>;
};
